import React, { useState, useEffect, useRef } from 'react';
import { useAlert } from './CustomAlert/AlertContext';
import Webcam from 'react-webcam';
import io from 'socket.io-client';
import { SIGNALING_SERVER, API_BASE, getPersonalInterviewScreenshotIntervalMs } from '../utils/config';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import * as LivekitClient from 'livekit-client';
import useScreenshotCapture from '../hooks/useScreenshotCapture';
// import TranscriptSaver from './TranscriptSaver';

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LinkExpiredView from './LinkExpiredView';
import CollaborativeWhiteboard from './interview/CollaborativeWhiteboard';
import CollaborativeIDE from './interview/CollaborativeIDE';
import { trackEvent, setUserProperties } from '../utils/analytics';


const CandidateView = ({ roomId, userName: propUserName }) => {
  const { showAlert, showConfirm } = useAlert();

  // ==================== PERSIST STATE KEY ====================
  const STORAGE_KEY = `interview_${roomId}_candidate`;

  // ==================== LOAD SAVED STATE OR USE DEFAULTS ====================
  const getInitialState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // console.log('✅ Restored saved state:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('❌ Error loading saved state:', error);
    }
    return {
      currentStep: 'name',
      candidateName: propUserName || '',
      calibrationCompleted: false
    };
  };

  const initialState = getInitialState();

  // ==================== FLOW STATES ====================
  const [currentStep, setCurrentStep] = useState(initialState.currentStep);
  const [candidateName, setCandidateName] = useState(initialState.candidateName);
  const [calibrationCompleted, setCalibrationCompleted] = useState(true); // Default to true to skip
  const [isInterviewEnded, setIsInterviewEnded] = useState(false);
  const [isCheckingLink, setIsCheckingLink] = useState(true);

  // Connection and Eye Tracking States
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Not connected');
  const [remoteInterviewers, setRemoteInterviewers] = useState([]);
  const [isLookingAway, setIsLookingAway] = useState(false);
  const [lookAwayCount, setLookAwayCount] = useState(0);
  const [gazeStatus, setGazeStatus] = useState('Not Started');
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [calibrationStep, setCalibrationStep] = useState(0);
  const [isGazeActive, setIsGazeActive] = useState(false);
  const [currentGaze, setCurrentGaze] = useState({ x: 0, y: 0 });
  const [gazeDirection, setGazeDirection] = useState('CENTER'); // Added for MediaPipe

  // Speech Recognition and AI Detection States
  const [transcribedText, setTranscribedText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [aiDetectionScore, setAiDetectionScore] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentModel, setCurrentModel] = useState('');
  const [detectionMethod, setDetectionMethod] = useState('');
  const [isWebGazerLoaded, setIsWebGazerLoaded] = useState(false);

  // Pause Detection States
  // const [lastSpeechTime, setSecondsSinceLastSpeech] = useState(Date.now());
  // const [secondsSinceLastSpeech, setSecondsSinceLastSpeech] = useState(0);

  // Video Recording States
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [screenshotIntervalMs, setScreenshotIntervalMs] = useState(getPersonalInterviewScreenshotIntervalMs());
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const videoIntervalRef = useRef(null);

  // Screen Sharing State
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const localScreenShareRef = useRef(null);
  const [isPipCollapsed, setIsPipCollapsed] = useState(false);

  // ====== COLLABORATIVE FEATURES STATE ======
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [showIDE, setShowIDE] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // 'whiteboard' | 'ide' | null
  const activePanelRef = useRef(null); // Mirror of activePanel for stale-closure-safe reads

  // Toggle panel — syncs state across clients so opening on one side opens on the other
  const togglePanel = (panel, isFromRemote = false) => {
    // Use ref to avoid stale closure bug when called from socket listeners registered at mount
    let targetPanel = activePanelRef.current === panel ? null : panel;

    // If coming from remote command, align state exactly to payload
    if (isFromRemote) {
      targetPanel = panel; // can be 'whiteboard', 'ide', or null
    }

    activePanelRef.current = targetPanel; // Keep ref in sync
    setActivePanel(targetPanel);
    setShowWhiteboard(targetPanel === 'whiteboard');
    setShowIDE(targetPanel === 'ide');

    // Emit UI state to opposite party
    if (!isFromRemote && socketRef.current) {
      socketRef.current.emit('sync-panel', { panel: targetPanel });
    }
  };

  // Refs
  const webcamRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const livekitRoomRef = useRef(null);
  const socketRef = useRef(null);
  const localStreamRef = useRef(null);
  const interviewerVideoTracksRef = useRef({}); // identity -> LiveKit track, for deferred video attachment

  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [mutedParticipants, setMutedParticipants] = useState(new Set());

  const toggleMic = async () => {
    if (livekitRoomRef.current && livekitRoomRef.current.localParticipant) {
      const newState = !isMicEnabled;
      await livekitRoomRef.current.localParticipant.setMicrophoneEnabled(newState);
      setIsMicEnabled(newState);
    }
  };

  // MediaPipe Refs to prevent double-init WebAssembly crashes in Strict Mode
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const mpInitActiveRef = useRef(false);
  const lookAwayTimerRef = useRef(null);
  const webgazerInitialized = useRef(false);
  const cleanupExecuted = useRef(false);
  const isLookingAwayRef = useRef(false);
  const smoothedGazeRef = useRef({ h: 0.5, v: 0.5 });
  const neutralVerticalRef = useRef({ value: 0.5, samples: 0 });
  // const pauseTimerRef = useRef(null);
  const lastTranscriptRef = useRef('');
  const screenshotStartedRef = useRef(false);
  // const PAUSE_THRESHOLD = 10000;
  const speechStartedRef = useRef(false);  // ✅ ADD THIS LINE
  const currentStepRef = useRef(currentStep);

  const batchTimerRef = useRef(null);

  // ==================== SCREENSHOT CAPTURE HOOK ====================
  const {
    startCapture: startScreenshotCapture,
    stopCapture: stopScreenshotCapture,
    captureOnEnd: captureScreenshotOnEnd,
    captureNow: captureScreenshotNow,
    isCapturing: isCapturingScreenshots,
    captureCount: screenshotCount,
  } = useScreenshotCapture({
    interviewId: roomId,
    userId: propUserName || candidateName,
    role: 'candidate',
    captureInterval: screenshotIntervalMs,
    maxWidth: 1920,
    quality: 0.7,
    maxCaptures: Infinity,
    enabled: true,
    addWatermark: true,
    captureMode: 'viewport',
  });

  useEffect(() => {
    const refreshInterval = () => {
      const latest = getPersonalInterviewScreenshotIntervalMs();
      setScreenshotIntervalMs((prev) => (prev === latest ? prev : latest));
    };

    refreshInterval();

    const storageHandler = (event) => {
      if (!event.key || event.key === 'screenshot_interval_personal_ms') {
        refreshInterval();
      }
    };

    window.addEventListener('storage', storageHandler);
    const polling = window.setInterval(refreshInterval, 2000);

    return () => {
      window.removeEventListener('storage', storageHandler);
      window.clearInterval(polling);
    };
  }, []);


  // Speech Recognition Hook
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  const calibrationPoints = [
    { x: 10, y: 10 },
    { x: 90, y: 10 },
    { x: 10, y: 90 },
    { x: 90, y: 90 }
  ];

  const proxyUrl = `${API_BASE}/api/auth/ai-detect`;
  const model = 'roberta-base-openai-detector';

  // ==================== SAVE STATE TO LOCALSTORAGE ====================

  useEffect(() => {
    const stateToSave = {
      currentStep,
      candidateName,
      calibrationCompleted
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    console.log('💾 Saved state to localStorage:', stateToSave);

    // ✅ Keep ref in sync for event listeners
    currentStepRef.current = currentStep;

  }, [currentStep, candidateName, calibrationCompleted, STORAGE_KEY]);

  // ==================== COMPONENT MOUNT LOGGER ====================

  useEffect(() => {
    console.log('🔵 ========== CANDIDATE VIEW MOUNTED ==========');
    console.log('Room ID:', roomId);
    console.log('Current Step:', currentStep);
    console.log('Candidate Name:', candidateName);
    console.log('Calibration Completed:', calibrationCompleted);

    return () => {
      console.log('🔴 ========== CANDIDATE VIEW UNMOUNTED ==========');
    };
  }, []);

  // ==================== CHECK LINK EXPIRATION ====================
  useEffect(() => {
    const checkRoomStatus = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/status`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'COMPLETED') {
            console.warn('⚠️ Room is already completed. Link expired.');
            // If candidate is/was already in interview flow, show completion screen instead of expired.
            if (currentStepRef.current === 'meeting' || currentStepRef.current === 'calibration') {
              setIsInterviewEnded(true);
            } else {
              setCurrentStep('expired');
            }
          }
        } else if (res.status === 404) {
          console.warn('⚠️ Room not found. Link expired.');
          setCurrentStep('expired');
        }
      } catch (e) {
        console.error('Failed to check link status', e);
      } finally {
        setIsCheckingLink(false);
      }
    };
    checkRoomStatus();
  }, [roomId]);

  // ==================== SKIP TO MEETING IF ALREADY CALIBRATED ====================

  useEffect(() => {
    if (calibrationCompleted && currentStep === 'calibration') {
      console.log('✅ Calibration already completed, skipping to meeting...');
      setTimeout(() => {
        setCurrentStep('meeting');
      }, 500);
    }
  }, [calibrationCompleted, currentStep]);

  useEffect(() => {
    if (currentStep === 'calibration') {
      neutralVerticalRef.current = { value: 0.5, samples: 0 };
    }
  }, [currentStep]);

  // ==================== LOAD MEDIAPIPE ON CALIBRATION STEP (WebGazer Commented out) ====================

  useEffect(() => {
    // Skip if already calibrated
    if (calibrationCompleted) {
      console.log('⏭️ Skipping MediaPipe initialization - already calibrated');
      return;
    }

    if (currentStep !== 'calibration' || webgazerInitialized.current) return;

    console.log('👁️ ========== INITIALIZING MEDIAPIPE ==========');

    const initMediaPipe = async () => {
      try {
        await setupMediaPipeGazeTracking();
        webgazerInitialized.current = true; // reusing this ref to prevent double init
        setIsWebGazerLoaded(true); // Reusing this state to enable skip button
        setGazeStatus('Ready to start');
      } catch (error) {
        console.error('❌ Failed to load MediaPipe:', error);
        showAlert('Failed to load eye tracking. Please refresh.', 'error');
      }
    };

    initMediaPipe();

  }, [currentStep, calibrationCompleted]);

  const loadWebGazer = () => {
    /* WEBGAZER COMMENTED OUT
    console.log('👁️ Loading WebGazer...');
    // ...
    */
    return Promise.resolve();
  };

  // ==================== INITIALIZE CONNECTION & MEDIAPIPE ====================
  useEffect(() => {
    if (currentStep !== 'meeting') return;

    console.log('🔌 Meeting step - Initializing connection and MediaPipe...');
    let mounted = true;
    let tabDetectionCleanup = null;
    const init = async () => {
      if (!mounted) return;
      await initializeConnection();
      tabDetectionCleanup = setupTabDetection();

      // Start MediaPipe immediately in the meeting step
      try {
        await setupMediaPipeGazeTracking();
        setIsGazeActive(true);
        setGazeStatus('✅ Active');
      } catch (e) {
        console.error('❌ Failed to start MediaPipe tracking:', e);
      }

    };
    init();
    return () => {
      mounted = false;
      if (tabDetectionCleanup) tabDetectionCleanup();
      screenshotStartedRef.current = false;
      cleanupConnection();
      stopScreenshotCapture();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [currentStep]);

  // Fallback: if socket event is delayed/missed, detect completion from backend and show thank-you immediately.
  useEffect(() => {
    if (currentStep !== 'meeting' || isInterviewEnded) return;

    let active = true;

    const checkCompletion = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/status`);
        if (!res.ok) return;

        const roomStatus = await res.json();
        if (!active) return;

        if (roomStatus.status === 'COMPLETED') {
          setIsInterviewEnded(true);
          cleanupConnection();
        }
      } catch (error) {
        console.warn('⚠️ Candidate completion status poll failed:', error);
      }
    };

    // Run once immediately, then poll every 2s during meeting.
    checkCompletion();
    const intervalId = window.setInterval(checkCompletion, 2000);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [currentStep, isInterviewEnded, roomId]);

  // Start screenshot capture only when interviewer is actually connected.
  useEffect(() => {
    if (currentStep !== 'meeting') return;
    if (remoteInterviewers.length === 0) return;
    if (screenshotStartedRef.current) return;

    const videoElement = webcamRef.current?.video || document.getElementById('candidate-local-video');
    if (!videoElement) return;

    screenshotStartedRef.current = true;
    console.log('📸 Starting candidate screenshot capture (interviewer connected)...');
    startScreenshotCapture(videoElement);
  }, [currentStep, remoteInterviewers.length, startScreenshotCapture]);

  // ==================== VIDEO RECORDING LOGIC ====================
  const startVideoRecording = (durationMs) => {
    if (!localStreamRef.current) {
      console.warn('⚠️ Cannot record video: No local stream available.');
      return;
    }
    if (isRecordingVideo || (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording')) {
      // Silently skip if already recording
      return;
    }

    try {
      console.log(`🎥 Speech detected: Started recording ${durationMs / 1000}s video snippet for Sync verification...`);
      setIsRecordingVideo(true);
      recordedChunksRef.current = [];

      const options = { mimeType: 'video/webm; codecs=vp8,opus' };
      const recorder = new MediaRecorder(localStreamRef.current, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        setIsRecordingVideo(false);
        console.log('🛑 Video snippet finalized. Sending to Sync API...');
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });

        // Prepare FormData for the Express endpoint
        const formData = new FormData();
        formData.append('file', blob, 'sync_snippet.webm');

        const syncUrl = `${API_BASE}/api/video-sync/verify-sync`;

        try {
          const response = await fetch(syncUrl, {
            method: 'POST',
            body: formData,
            // Header Content-Type is auto-set by the browser for FormData
          });

          if (!response.ok) throw new Error(`API responded with ${response.status}`);

          const result = await response.json();
          console.log('✅ Sync Verification Result:', result);

          // If the model couldn't find a face or failed to process, skip alert
          if (result.status === 'inconclusive' || result.overall_score === undefined) {
            console.warn('⚠️ Sync Check was inconclusive (No face detected or processing error). Skipping alert.');
            return;
          }

          if (result.is_synced) {
            successfulChecksCountRef.current += 1;
            console.log(`✅ Lip sync verified successfully! (${successfulChecksCountRef.current}/3 completed).`);
            if (successfulChecksCountRef.current >= 3) {
              console.log('🛑 All 3 lip sync checks passed. Permanent stop to future checks.');
            }
          }

          // Alert the interviewer if Lip Sync fails or passes
          const message = result.is_synced
            ? `Candidate is speaking (Audio-Video Sync ${successfulChecksCountRef.current}/3 Verified ✅)`
            : 'Lip-Sync Mismatch Detected! Audio may be artificial. ❌';

          const severity = result.is_synced ? 'info' : 'critical';

          sendAlert({
            roomId,
            type: 'LIP_SYNC_RESULT',
            message: message,
            severity: severity,
            timestamp: new Date().toISOString(),
            aiData: {
              aiScore: result.overall_score.toFixed(2),
              method: 'Video Analysis (Gemini Flash)',
              geminiReason: result.gemini_reason
            }
          });

        } catch (err) {
          console.error('❌ Sync API failed:', err);
        }
      };

      recorder.start();

      // Stop recording after durationMs
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, durationMs);

    } catch (e) {
      console.error('❌ Failed to start video recording:', e);
      setIsRecordingVideo(false);
    }
  };

  // ==================== TRIGGER RECORDING ON SPEECH ====================
  // We use the transcript length to see if they started speaking newly
  const prevTranscriptLengthRef = useRef(0);
  const lastSyncRecordingTimeRef = useRef(0); // Added for throttling

  const successfulChecksCountRef = useRef(0);
  const meetingStartTimeRef = useRef(null);
  const targetCheckTimesRef = useRef([]);

  useEffect(() => {
    if (currentStep === 'meeting' && !meetingStartTimeRef.current) {
      meetingStartTimeRef.current = Date.now();
      // Check 1: 0 mins
      // Check 2: Random between 2 and 5 mins
      const check2Offset = Math.floor(Math.random() * (5 - 2 + 1) + 2) * 60000;
      // Check 3: Random between 6 and 9 mins
      const check3Offset = Math.floor(Math.random() * (9 - 6 + 1) + 6) * 60000;

      targetCheckTimesRef.current = [0, check2Offset, check3Offset];
      console.log('🕒 Sync check targets generated (ms offsets):', targetCheckTimesRef.current);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep !== 'meeting' || !transcript || successfulChecksCountRef.current >= 3) return;
    if (!meetingStartTimeRef.current) return;

    // Check if 10 minutes exceeded
    const timeSinceMeetingStart = Date.now() - meetingStartTimeRef.current;
    if (timeSinceMeetingStart > 10 * 60 * 1000) return;

    const currentLen = transcript.length;
    // If transcript grew (they are speaking) and we aren't already recording
    if (currentLen > prevTranscriptLengthRef.current) {
      const timeSinceLastRecording = Date.now() - lastSyncRecordingTimeRef.current;
      const currentTargetOffset = targetCheckTimesRef.current[successfulChecksCountRef.current];

      // Ensure we've reached the target time for this specific check, AND respected 60s retry cooldown
      if (!isRecordingVideo && timeSinceLastRecording >= 60000 && timeSinceMeetingStart >= currentTargetOffset) {
        lastSyncRecordingTimeRef.current = Date.now();
        startVideoRecording(10000); // Record a 10s chunk to verify sync
      }
    }
    prevTranscriptLengthRef.current = currentLen;
  }, [transcript, currentStep]);

  // ✅ Ref to access latest transcript in interval
  const transcriptRef = useRef(transcript);

  // Sync ref with transcript
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  // Force Save Removed as per user request
  // const forceSaveTranscript = () => { ... }

  // ==================== AUTO-START SPEECH RECOGNITION ====================
  useEffect(() => {
    if (currentStep === 'meeting' && browserSupportsSpeechRecognition && !speechStartedRef.current) {
      console.log('✅ Auto-starting speech recognition...');
      setTimeout(() => {
        startSpeechRecognition();
        speechStartedRef.current = true;
      }, 2000); // Wait 2 seconds after entering meeting
    }
  }, [currentStep, browserSupportsSpeechRecognition]);

  // ✅ ROBUST DISCONNECT HANDLING: Restart speech recognition if it stops unexpectedly
  useEffect(() => {
    if (isRecording && !listening) {
      console.warn('⚠️ Candidate speech recognition stopped unexpectedly. Resuming without wipe...');
      // Start listening directly without resetting our local transcript history
      SpeechRecognition.startListening({
        continuous: true,
        language: 'en-US',
        interimResults: true,
        clearTranscriptOnListen: false
      });
    }
  }, [listening, isRecording]);

  // ==================== REAL-TIME AI ANALYSIS (SILENCE DETECTION) ====================
  const aiDebounceTimerRef = useRef(null);

  useEffect(() => {
    // Only run if they are actively in the meeting and the mic is picking up words
    if (!listening || !transcript) return;

    // Clear the existing timer because the candidate is still speaking
    if (aiDebounceTimerRef.current) {
      clearTimeout(aiDebounceTimerRef.current);
    }

    // Set a new timer. If they stop speaking for 5 seconds, this will fire.
    aiDebounceTimerRef.current = setTimeout(() => {
      const fullText = transcriptRef.current; // Read from ref
      if (!fullText) return;

      const lastLength = lastTranscriptRef.current.length;

      // Safety check: if Speech API reset its internal buffer
      if (fullText.length < lastLength) {
        console.warn('⚠️ Transcript reset detected. Resetting cursor.');
        lastTranscriptRef.current = '';
      }

      // Slice out ONLY the newly spoken words since the last analysis
      const spoken = fullText.slice(lastTranscriptRef.current.length).trim();

      // Only send if there's substantial new content
      if (spoken.length > 0) {
        console.log(`⏱️ Silence detected (5s). Auto-analyzing ${spoken.length} chars...`);
        analyzeTextWithAI(spoken);   // send ONLY the new chunk for analysis
        lastTranscriptRef.current = fullText; // Move the cursor forward
      }
    }, 5000); // Wait 5 seconds of silence before firing

    return () => {
      if (aiDebounceTimerRef.current) {
        clearTimeout(aiDebounceTimerRef.current);
      }
    };
  }, [transcript, listening]); // Runs every time a new word is recognized

  // ==================== FIX VIDEO VISIBILITY ====================
  useEffect(() => {
    if (currentStep !== 'meeting') return;

    console.log('🔧 Monitoring video visibility...');

    const interval = setInterval(() => {
      const remoteVideo = document.getElementById('interviewer-remote-video');
      const localVideo = document.getElementById('candidate-local-video');

      if (remoteVideo && (remoteVideo.style.display === 'none' || remoteVideo.style.visibility === 'hidden')) {
        console.warn('⚠️ Interviewer video hidden! Restoring...');
        remoteVideo.style.display = 'block';
        remoteVideo.style.visibility = 'visible';
      }

      if (localVideo && (localVideo.style.display === 'none' || localVideo.style.visibility === 'hidden')) {
        console.warn('⚠️ Candidate video hidden! Restoring...');
        localVideo.style.display = 'block';
        localVideo.style.visibility = 'visible';
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [currentStep]);

  // ==================== CLEANUP ON UNMOUNT ====================
  useEffect(() => {
    return () => {
      if (!cleanupExecuted.current) {
        cleanupWebGazer();
        cleanupExecuted.current = true;
      }
    };
  }, []);

  // ==================== PREVENT REFRESH ====================
  useEffect(() => {
    const handleKeyDown = async (e) => {
      if ((e.key === 'F5') || (e.ctrlKey && e.key.toLowerCase() === 'r') || (e.metaKey && e.key.toLowerCase() === 'r')) {
        if (currentStep === 'meeting' && !isInterviewEnded) {
          e.preventDefault();
          const confirmed = await showConfirm('Are you sure you want to reload? Your interview is in progress.');
          if (confirmed) {
            window.location.reload();
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, isInterviewEnded, showConfirm]);

  useEffect(() => {
    if (currentStep === 'meeting' && !isInterviewEnded) {
      const handleBeforeUnload = (e) => {
        const message = "Are you sure you want to leave? Your interview is in progress.";
        e.preventDefault();
        e.returnValue = message;
        return message;
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [currentStep, isInterviewEnded]);

  //===================== save the answers to db ==============
  const saveAnswerToDatabase = async ({ roomId, candidateName, answerText, wordCount, aiScore, detectionMethod, modelUsed }) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/answers/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId,
          candidateName,
          answerText,
          wordCount,
          aiScore,
          detectionMethod,
          modelUsed
        })
      });

      // SAFE PARSING
      const contentType = response.headers.get("content-type");
      let data = {};
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        console.warn('⚠️ saveAnswer returned non-JSON:', await response.text());
      }

      if (response.ok && data.answerId) {
        console.log('💾 Answer saved to DB with ID:', data.answerId);

        // ✅ GENERATE COMBINED TRANSCRIPT
        try {
          const transcriptRes = await fetch(`${API_BASE}/api/conversation/generate-transcript`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId })
          });

          if (transcriptRes.ok) {
            console.log('📝 Combined transcript updated');
          } else {
            console.warn('⚠️ Transcript generation failed');
          }
        } catch (transcriptErr) {
          console.error('❌ Transcript generation error:', transcriptErr);
        }
      } else {
        console.error('❌ Failed to save answer:', data.error || 'Unknown error');
      }
    } catch (error) {
      console.error('❌ Network error saving answer:', error);
    }
  };

  // ==================== STEP 1: NAME ENTRY ====================
  const handleNameSubmit = async () => {
    if (!candidateName.trim()) {
      showAlert('Please enter your name', 'error');
      return;
    }
    console.log('✅ Name entered:', candidateName);

    try {
      const response = await fetch(`${API_BASE}/api/auth/rooms/update-candidate-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, candidateName: candidateName.trim() })
      });

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const data = await response.json();
        if (response.ok) {
          console.log('💾 Candidate name saved:', data.message);
        } else {
          console.error('❌ Save failed:', data.error);
        }
      } else {
        const text = await response.text();
        console.error('❌ Expected JSON but got:', text.substring(0, 100)); // Log first 100 chars
        if (!response.ok) throw new Error(`Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Network error updated candidate name:', error);
      // We continue anyway to not block the interview
    }

    console.log('➡️ Moving to meeting room (calibration skipped)');
    setCurrentStep('meeting');
  };

  const startCalibration = async () => {
    console.log('🎯 ========== STARTING MEDIAPIPE TRACKING ==========');

    // We seamlessly trigger completion since MediaPipe does not need point-click calibration
    setIsCalibrating(true);
    setGazeStatus('Initializing camera... (Please allow access)');

    try {
      // First, request camera permissions
      console.log('📷 Requesting camera permissions...');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
        // Stop the temporary stream - MediaPipe will manage its own via the <video> element
        stream.getTracks().forEach(track => track.stop());
        console.log('✅ Camera access granted');
      } catch (e) {
        console.error('❌ Camera permission denied:', e);
        // Don't block - just skip calibration
        setIsCalibrating(false);
        setCalibrationCompleted(true);
        setCurrentStep('meeting');
        return;
      }

      console.log('✅ Camera initialization phase complete');
      setGazeStatus('Detecting face...');

      await new Promise(r => setTimeout(r, 1000));
      await finishCalibration();

    } catch (error) {
      console.error('❌ Initialization failed:', error);
      console.error('❌ Error details:', error.message, error.stack);

      setGazeStatus(`⚠️ Error: ${error.message || 'Initialization failed'}. Please retry.`);
      setIsCalibrating(false); // Stop the spinner/calibration mode
    }
  };

  const waitForFaceDetection = () => {
    return Promise.resolve(); // Not needed for MediaPipe init
  };

  const handleCalibrationClick = async (point, index) => {
    // Not needed for MediaPipe
  };

  const finishCalibration = async () => {
    console.log('🏁 Finishing Initialization...');
    setIsCalibrating(false);
    setGazeStatus('Finalizing...');

    await new Promise(r => setTimeout(r, 500));

    // ✅ MARK CALIBRATION AS COMPLETED
    setCalibrationCompleted(true);
    console.log('✅ Tracking marked as completed');

    setIsGazeActive(true);
    setCurrentStep('meeting');
  };
  // Helper function reused by the MediaPipe tracker to emit socket alerts
  const handleGazeAlert = (level, direction) => {
    if (level === 'ALERT' && !isLookingAwayRef.current) {
      // Transition from Safe/Warn -> Alert
      isLookingAwayRef.current = true;
      setIsLookingAway(true);
      setGazeDirection(direction);
      lookAwayTimerRef.current = setTimeout(() => {
        sendAlert({
          roomId,
          type: 'LOOKING_AWAY',
          message: 'Candidate looking away',
          severity: 'high',
          timestamp: new Date().toISOString(),
          gazeData: { direction }
        });
        setLookAwayCount(prev => prev + 1);
      }, 1500); // 1.5s tolerance for looking away (Fast Alerts)
    } else if (level === 'WARN' && !isLookingAwayRef.current) {
      // Intermittent warning state, do not trigger a full socket alert immediately,
      // but maybe notify the interviewer of caution
      setGazeDirection(direction);

    } else if (level === 'SAFE' && isLookingAwayRef.current) {
      // Recovered
      if (lookAwayTimerRef.current) clearTimeout(lookAwayTimerRef.current);
      isLookingAwayRef.current = false;
      setIsLookingAway(false);
      setGazeDirection('CENTER');
    }
  };

  const setupMediaPipeGazeTracking = async () => {
    if (window.globalFaceMeshInstance) {
      console.log('👁️ MediaPipe FaceMesh already running (globally cached).');
      faceMeshRef.current = window.globalFaceMeshInstance;

      const videoElement = webcamRef.current?.video || document.getElementById('candidate-local-video');
      if (videoElement && window.Camera && !cameraRef.current) {
        try {
          const camera = new window.Camera(videoElement, {
            onFrame: async () => {
              if (currentStepRef.current === 'meeting' && faceMeshRef.current) {
                try { await faceMeshRef.current.send({ image: videoElement }); }
                catch (e) { }
              }
            },
            width: 640,
            height: 480
          });
          cameraRef.current = camera;
          camera.start();
          console.log('✅ MediaPipe Camera Loop started (from cache)');
        } catch (err) {
          console.error('❌ Failed to start MediaPipe camera:', err);
        }
      }
      return;
    }

    if (window.globalFaceMeshLoading) {
      console.log('⏳ MediaPipe FaceMesh is already loading globally. Skipping duplicate init.');
      return;
    }

    if (faceMeshRef.current) {
      console.log('👁️ MediaPipe FaceMesh already running (cached).');
      return;
    }
    if (mpInitActiveRef.current) {
      console.log('⏳ MediaPipe init already in progress. Skipping duplicate call.');
      return;
    }

    console.log('👁️ Setting up MediaPipe FaceMesh & Iris tracking...');
    mpInitActiveRef.current = true;
    window.globalFaceMeshLoading = true;

    if (!window.FaceMesh) {
      console.error('❌ MediaPipe FaceMesh not loaded from CDN');
      mpInitActiveRef.current = false;
      window.globalFaceMeshLoading = false;
      return;
    }

    try {
      // Explicitly set the path version to avoid CDN caching mismatch bugs
      const faceMesh = new window.FaceMesh({
        locateFile: (file) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619/${file}`;
        }
      });
      faceMeshRef.current = faceMesh;
      window.globalFaceMeshInstance = faceMesh;
      window.globalFaceMeshLoading = false;

      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true, // Crucial for Iris tracking (gives landmarks 468-477)
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      faceMesh.onResults((results) => {
        if (currentStepRef.current !== 'meeting') return;

        if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
          const lm = results.multiFaceLandmarks[0];

          let direction = 'CENTER';
          let alertLevel = 'SAFE';

          // ===== HEAD POSE DETECTION =====
          const noseTip = lm[1];
          const faceLeft = lm[234];
          const faceRight = lm[454];
          const faceTop = lm[10];
          const faceBottom = lm[152];

          const faceCenterX = (faceLeft.x + faceRight.x) / 2;
          const faceCenterY = (faceTop.y + faceBottom.y) / 2;
          const faceWidth = Math.abs(faceRight.x - faceLeft.x);
          const faceHeight = Math.abs(faceBottom.y - faceTop.y);

          const noseOffsetX = (noseTip.x - faceCenterX) / faceWidth;
          const noseOffsetY = (noseTip.y - faceCenterY) / faceHeight;

          // Head pose thresholds
          const H_HEAD = 0.22;
          const V_HEAD = 0.16;

          const headFlags = [];
          if (noseOffsetX < -H_HEAD) headFlags.push('← HEAD LEFT');
          if (noseOffsetX > H_HEAD) headFlags.push('HEAD RIGHT →');
          if (noseOffsetY < -V_HEAD) headFlags.push('↑ HEAD UP');
          if (noseOffsetY > V_HEAD) headFlags.push('↓ HEAD DOWN');

          if (headFlags.length > 0) {
            direction = headFlags.join(' + ');
            alertLevel = 'ALERT';
          }

          // ===== IRIS-BASED EYE GAZE DETECTION =====
          if (lm.length > 473 && alertLevel === 'SAFE') {
            const rIris = lm[468];
            const lIris = lm[473];

            // Right eye geometry (Restored eyelid tracking for natural eye stretch/squint compensation)
            const re_outer = lm[33];
            const re_inner = lm[133];
            const re_top = lm[159];
            const re_bot = lm[145];
            const reMinX = Math.min(re_outer.x, re_inner.x);
            const reMaxX = Math.max(re_outer.x, re_inner.x);
            const reMinY = Math.min(re_top.y, re_bot.y);
            const reMaxY = Math.max(re_top.y, re_bot.y);
            const reW = Math.max(1e-6, (reMaxX - reMinX));
            const reH = Math.max(1e-6, (reMaxY - reMinY));
            const reHR = (rIris.x - reMinX) / reW;
            const reVR = (rIris.y - reMinY) / reH;

            // Left eye geometry
            const le_inner = lm[362];
            const le_outer = lm[263];
            const le_top = lm[386];
            const le_bot = lm[374];
            const leMinX = Math.min(le_outer.x, le_inner.x);
            const leMaxX = Math.max(le_outer.x, le_inner.x);
            const leMinY = Math.min(le_top.y, le_bot.y);
            const leMaxY = Math.max(le_top.y, le_bot.y);
            const leW = Math.max(1e-6, (leMaxX - leMinX));
            const leH = Math.max(1e-6, (leMaxY - leMinY));
            const leHR = (lIris.x - leMinX) / leW;
            const leVR = (lIris.y - leMinY) / leH;

            const rawH = (reHR + leHR) / 2;
            const rawV = (reVR + leVR) / 2;

            // Smooth noisy vertical movement to improve top/bottom stability.
            const alpha = 0.35;
            smoothedGazeRef.current.h = smoothedGazeRef.current.h * (1 - alpha) + rawH * alpha;
            smoothedGazeRef.current.v = smoothedGazeRef.current.v * (1 - alpha) + rawV * alpha;

            const avgH = smoothedGazeRef.current.h;
            const avgV = smoothedGazeRef.current.v;

            // Build neutral vertical baseline from centered/stable frames only.
            const canUseForVerticalCalibration =
              avgH > 0.45 && avgH < 0.55 &&
              Math.abs(noseOffsetY) < 0.05 &&
              Math.abs(noseOffsetX) < 0.08;

            if (canUseForVerticalCalibration) {
              if (neutralVerticalRef.current.samples < 120) {
                const prev = neutralVerticalRef.current.value;
                neutralVerticalRef.current.value = prev * 0.92 + avgV * 0.08;
                neutralVerticalRef.current.samples += 1;
              } else {
                // Slow adaptation for small posture/camera drift after calibration.
                neutralVerticalRef.current.value = neutralVerticalRef.current.value * 0.995 + avgV * 0.005;
              }
            }

            // const vDelta = avgV - neutralVerticalRef.current.value;
            const vDelta = avgV - neutralVerticalRef.current.value;

            // Dynamic scaling based on device aspect ratio
            const aspectRatio = window.innerWidth / window.innerHeight;
            // Base aspect ratio of 16:9 (1.77) for typical laptops
            const hScale = Math.min(1.5, Math.max(0.5, aspectRatio / 1.77));
            // For vertical, if it's a tall mobile screen, vertical distance to the edge is larger
            const vScale = Math.min(1.5, Math.max(0.5, 1.77 / aspectRatio));

            const hAlertDist = 0.12 * hScale;
            const hWarnDist = 0.06 * hScale;

            const EYE_H_MIN_ALERT = 0.5 - hAlertDist; // Base was 0.38
            const EYE_H_MAX_ALERT = 0.5 + hAlertDist; // Base was 0.62

            const EYE_H_MIN_WARN = 0.5 - hWarnDist; // Base was 0.44
            const EYE_H_MAX_WARN = 0.5 + hWarnDist; // Base was 0.56

            // Vertical thresholds use per-user deltas for better UP/DOWN consistency.
            const vUpAlertDist = 0.03 * vScale;
            const vDownAlertDist = 0.10 * vScale;
            const vUpWarnDist = 0.02 * vScale;
            const vDownWarnDist = 0.065 * vScale;

            const EYE_V_UP_ALERT_DELTA = -vUpAlertDist;
            const EYE_V_DOWN_ALERT_DELTA = vDownAlertDist;
            const EYE_V_UP_WARN_DELTA = -vUpWarnDist;
            const EYE_V_DOWN_WARN_DELTA = vDownWarnDist;

            // Check ALERTS
            if (avgH < EYE_H_MIN_ALERT) { direction = '👁️ EYES LEFT'; alertLevel = 'ALERT'; }
            else if (avgH > EYE_H_MAX_ALERT) { direction = '👁️ EYES RIGHT'; alertLevel = 'ALERT'; }
            else if (vDelta < EYE_V_UP_ALERT_DELTA) { direction = (direction !== 'CENTER' ? direction + ' + ' : '') + '👁️ EYES UP'; alertLevel = 'ALERT'; }
            else if (vDelta > EYE_V_DOWN_ALERT_DELTA) { direction = (direction !== 'CENTER' ? direction + ' + ' : '') + '👁️ EYES DOWN'; alertLevel = 'ALERT'; }

            // Check WARNS (gentle glance)
            else if (avgH < EYE_H_MIN_WARN) { direction = '← Glancing Left'; alertLevel = 'WARN'; }
            else if (avgH > EYE_H_MAX_WARN) { direction = 'Glancing Right →'; alertLevel = 'WARN'; }
            else if (vDelta < EYE_V_UP_WARN_DELTA) { direction = '↑ Glancing Up'; alertLevel = 'WARN'; }
            else if (vDelta > EYE_V_DOWN_WARN_DELTA) { direction = '↓ Glancing Down'; alertLevel = 'WARN'; }
          }

          handleGazeAlert(alertLevel, direction);

        } else {
          // No face detected -> ALERT
          handleGazeAlert('ALERT', '❌ NO FACE DETECTED');
        }
      });

    } catch (err) {
      window.globalFaceMeshLoading = false;
      console.error('❌ Failed to initialize FaceMesh:', err);
    }

    // Start sending frames to MediaPipe once the local video starts playing
    const videoElement = webcamRef.current?.video || document.getElementById('candidate-local-video');

    if (videoElement && window.Camera && !cameraRef.current) {
      try {
        const camera = new window.Camera(videoElement, {
          onFrame: async () => {
            if (currentStepRef.current === 'meeting' && faceMeshRef.current) {
              try { await faceMeshRef.current.send({ image: videoElement }); }
              catch (e) { /* ignore rapid unmount errs */ }
            }
          },
          width: 640,
          height: 480
        });
        cameraRef.current = camera;
        camera.start();
        console.log('✅ MediaPipe Camera Loop started');
      } catch (err) {
        console.error('❌ Failed to start MediaPipe camera:', err);
      }
    } else if (!window.Camera) {
      console.warn('⚠️ MediaPipe Camera utils not loaded');
    }

    mpInitActiveRef.current = false; // Release lock
  };

  // ==================== [KEEP ALL YOUR OTHER FUNCTIONS THE SAME] ====================
  const startSpeechRecognition = () => {
    if (!browserSupportsSpeechRecognition) {
      console.error('❌ Browser does not support speech recognition');
      return;
    }

    console.log('🎤 Starting speech recognition...');
    resetTranscript();
    setIsRecording(true);
    setTranscribedText('');
    setAiDetectionScore(null);
    setDetectionMethod('');
    // setLastSpeechTime(Date.now());
    // setSecondsSinceLastSpeech(0);

    SpeechRecognition.startListening({
      continuous: true,
      language: 'en-US',
      interimResults: true
    });
  };
  const stopSpeechRecognition = async () => {
    console.log('🛑 Stopping speech recognition');
    SpeechRecognition.stopListening();
    setIsRecording(false);

    if (transcript.trim().length > 0) {
      setTranscribedText(transcript);
      await analyzeTextWithAI(transcript);
    }
  };
  // ❌ REMOVED: analyzeTextRuleBased (User requested complete removal)

  const analyzeTextWithAI = async (text) => {
    if (!text || text.trim().length < 10) return;

    console.log('🔍 Analyzing text with Azure AI...');
    setIsAnalyzing(true);

    const wordCount = text.trim().split(/\s+/).length;

    try {
      const apiResult = await tryHuggingFaceAPI(text);

      if (apiResult && apiResult.success) {
        // ✅ Azure Success
        const { score, model, suggestedQuestion } = apiResult;

        setAiDetectionScore(score);
        setCurrentModel(model);
        setDetectionMethod("Azure OpenAI");

        // Save to DB
        await saveAnswerToDatabase({
          roomId,
          candidateName,
          answerText: text.trim(),
          wordCount,
          aiScore: score,
          detectionMethod: "Azure OpenAI",
          modelUsed: model
        });

        // Send Alert
        sendAlert({
          roomId,
          type: "AI_DETECTION_RESULT",
          message: `AI Detection: ${score}% likelihood`,
          severity: score > 70 ? "high" : score > 50 ? "medium" : "low",
          timestamp: new Date().toISOString(),
          aiData: {
            aiScore: score,
            detectionMethod: "Azure OpenAI",
            model: model,
            textAnalyzed: text.substring(0, 300),
            fullTextLength: text.length,
            wordCount,
            suggestedQuestion
          },
        });

      } else {
        // ❌ Azure Failed - No Fallback
        console.warn('⚠️ Azure AI Analysis failed or returned invalid data. Skipping alert.');
      }

    } catch (error) {
      console.error('❌ Error in analyzeTextWithAI:', error);
    }
    setIsAnalyzing(false);
  };
  const tryHuggingFaceAPI = async (text) => {
    try {
      // console.log('📤 Sending to Azure AI:', { text: text.substring(0, 50), roomId });
      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, model, roomId }), // ✅ ADDED roomId
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error('❌ Azure API Request Failed:', response.status, errText);
        throw new Error('API Error');
      }
      return await response.json();
    } catch (error) {
      console.error('❌ tryHuggingFaceAPI error:', error);
      return null;
    }
  };
  const setupTabDetection = () => {
    const handler = () => {
      if (document.hidden) {
        sendAlert({
          roomId,
          type: 'TAB_SWITCHED',
          message: 'Candidate switched tabs or minimized browser',
          severity: 'critical',
          timestamp: new Date().toISOString()
        });
      }
    };

    const blurHandler = () => {
      sendAlert({
        roomId,
        type: 'WINDOW_BLUR',
        message: 'Candidate moved focus away from the interview window',
        severity: 'high',
        timestamp: new Date().toISOString()
      });
    };

    document.addEventListener('visibilitychange', handler);
    window.addEventListener('blur', blurHandler);

    return () => {
      document.removeEventListener('visibilitychange', handler);
      window.removeEventListener('blur', blurHandler);
    };
  };
  const sendAlert = (alertData) => {
    if (!socketRef.current?.connected) {
      console.error('❌ Socket not connected');
      return;
    }

    try {
      socketRef.current.emit('alert', alertData);
      console.log('✅ Alert sent:', alertData.type);
      trackEvent('proctoring_alert', {
        type: alertData.type,
        severity: alertData.severity || 'high',
        room_id: roomId
      });
    } catch (error) {
      console.error('❌ Error sending alert:', error);
    }
  };

  const toggleScreenShare = async () => {
    try {
      if (!livekitRoomRef.current) return;
      const localParticipant = livekitRoomRef.current.localParticipant;

      if (isScreenSharing) {
        await localParticipant.setScreenShareEnabled(false);
        setIsScreenSharing(false);
      } else {
        await localParticipant.setScreenShareEnabled(true);
        setIsScreenSharing(true);
      }
    } catch (e) {
      console.error("Failed to toggle screen share", e);
      setIsScreenSharing(false);
    }
  };

  useEffect(() => {
    if (isScreenSharing && localScreenShareRef.current && livekitRoomRef.current) {
      const localParticipant = livekitRoomRef.current.localParticipant;
      if (!localParticipant) return;
      const screenPub = Array.from(localParticipant.trackPublications.values())
        .find(p => p.source === LivekitClient.Track.Source.ScreenShare);
      if (screenPub && screenPub.track) {
        screenPub.track.attach(localScreenShareRef.current);
      }
    }
  }, [isScreenSharing]);

  const initializeConnection = async () => {
    console.log('🔌 Initializing connection...');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true
      });

      localStreamRef.current = stream;
      if (webcamRef.current?.video) webcamRef.current.video.srcObject = stream;
      setConnectionStatus('Connecting to Signaling...');

      const newSocket = io(SIGNALING_SERVER, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000
      });

      socketRef.current = newSocket;
      setSocket(newSocket);

      newSocket.on('connect', () => {
        console.log('✅ Socket connected:', newSocket.id);
        setConnectionStatus('Connected to Signaling');
        newSocket.emit('join-room', { roomId, role: 'candidate', userName: candidateName });
      });

      newSocket.on('connect_error', (err) => {
        console.error('❌ Socket connection error:', err.message, err);
        setConnectionStatus('Connection error: ' + err.message);
      });

      newSocket.on('room-joined', (data) => {
        console.log('✅ Joined room', data);
        setUserProperties({ user_type: 'Candidate' });
        trackEvent('join_room', { role: 'candidate', room_id: roomId });
        if (data.otherPeerId) {
          setConnectionStatus('Interviewer connected');
        } else {
          setConnectionStatus('Waiting for interviewer...');
        }
      });

      newSocket.on('peer-joined', (data) => {
        console.log('✅ Interviewer joined');
        setConnectionStatus('Interviewer connected');
      });

      newSocket.on('interview-ended', async () => {
        console.log('🛑 Interviewer ended the call.');
        try {
          // Capture final screenshot before cleanup, but do not block end-screen rendering on failure.
          await captureScreenshotOnEnd();
        } catch (error) {
          console.warn('⚠️ Final screenshot capture failed on interview-ended event:', error);
        } finally {
          setIsInterviewEnded(true);
          cleanupConnection();
        }
      });

      newSocket.on('disconnect', (reason) => {
        console.warn('⚠️ Disconnected:', reason);
        setConnectionStatus('Disconnected');
      });

      newSocket.on('alert', (alertData) => {
        if (alertData.type === 'DECLINE_END_INTERVIEW') {
          showAlert('The interviewer declined your request to end the interview.', 'error', 5000);
        }
      });

      // Sync UI Panel opens/closes with interviewer
      // IMPORTANT: candidates never receive 'playback' syncs — playback is interviewer-only
      newSocket.on('sync-panel', ({ panel }) => {
        if (panel === 'playback') return; // Block playback from being shown on candidate side
        console.log(`📡 Syncing panel visibility from remote: ${panel}`);
        togglePanel(panel, true);
      });

      // --- LiveKit Connection ---
      console.log('🎫 Fetching LiveKit token...');
      const tokenRes = await fetch(`${API_BASE}/api/auth/livekit-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, userName: candidateName || 'Candidate', role: 'candidate' })
      });
      const tokenData = await tokenRes.json();

      if (!tokenData.success || !tokenData.token || !tokenData.serverUrl) {
        throw new Error('Failed to get LiveKit token');
      }

      console.log('📹 Establishing LiveKit connection...');
      const room = new LivekitClient.Room({
        adaptiveStream: true,
        dynacast: true,
      });
      livekitRoomRef.current = room;

      room.on(LivekitClient.RoomEvent.LocalTrackPublished, (publication, participant) => {
        if (publication.source === LivekitClient.Track.Source.ScreenShare) {
          setIsScreenSharing(true);
          if (localScreenShareRef.current && publication.track) {
            publication.track.attach(localScreenShareRef.current);
          }
        }
      });

      room.on(LivekitClient.RoomEvent.LocalTrackUnpublished, (publication, participant) => {
        if (publication.source === LivekitClient.Track.Source.ScreenShare) {
          setIsScreenSharing(false);
          if (publication.track) {
            publication.track.detach();
          }
        }
      });

      room.on(LivekitClient.RoomEvent.TrackSubscribed, (track, publication, participant) => {
        console.log('📥 Track subscribed:', track.kind, participant.identity);
        if (track.kind === 'video') {
          if (participant.identity.startsWith('interviewer-')) {
            // Store the track so the JSX ref callback can attach it once the video element mounts
            interviewerVideoTracksRef.current[participant.identity] = track;
            setRemoteInterviewers(prev => {
              if (prev.find(p => p.identity === participant.identity)) return prev;
              return [...prev, participant];
            });
            // Also try attaching immediately if the video element already exists in the DOM
            const existingEl = document.querySelector(`[data-interviewer-id="${participant.identity}"]`);
            if (existingEl) {
              console.log('📺 Attaching interviewer video track immediately (element already in DOM)');
              track.attach(existingEl);
            }
          } else {
            // fallback if needed
            if (remoteVideoRef.current) track.attach(remoteVideoRef.current);
          }
          setConnectionStatus('✅ Video connected!');
        } else if (track.kind === 'audio') {
          const element = track.attach();
          document.body.appendChild(element);
        }
      });


      room.on(LivekitClient.RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        console.log('📤 Track unsubscribed:', track.kind, participant.identity);
        track.detach();
      });

      room.on(LivekitClient.RoomEvent.ParticipantDisconnected, (participant) => {
        console.log('🚪 Participant disconnected:', participant.identity);
        if (participant.identity.startsWith('interviewer-')) {
          setRemoteInterviewers(prev => prev.filter(p => p.identity !== participant.identity));
        }
      });

      room.on(LivekitClient.RoomEvent.Disconnected, () => {
        console.log('Disconnected from LiveKit room');
      });

      room.on(LivekitClient.RoomEvent.TrackMuted, (publication, participant) => {
        if (publication.kind === 'audio') {
          setMutedParticipants(prev => { const s = new Set(prev); s.add(participant.identity); return s; });
        }
      });

      room.on(LivekitClient.RoomEvent.TrackUnmuted, (publication, participant) => {
        if (publication.kind === 'audio') {
          setMutedParticipants(prev => { const s = new Set(prev); s.delete(participant.identity); return s; });
        }
      });

      await room.connect(tokenData.serverUrl, tokenData.token);
      console.log('Connected to LiveKit room', room.name);

      await room.localParticipant.enableCameraAndMicrophone();

      const cameraPub = room.localParticipant.getTrackPublication(LivekitClient.Track.Source.Camera);
      if (cameraPub && cameraPub.videoTrack && webcamRef.current?.video) {
        // we can leave react-webcam controlling the local video display
        // but just to be sure we don't mess up MediaPipe:
      }

    } catch (error) {
      console.error('❌ Connection error:', error);
      setConnectionStatus('Error: ' + error.message);
    }
  };

  const cleanupConnection = () => {
    console.log('🧹 Cleaning up...');

    // if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    if (lookAwayTimerRef.current) clearTimeout(lookAwayTimerRef.current);
    if (listening) SpeechRecognition.stopListening();
    if (livekitRoomRef.current) livekitRoomRef.current.disconnect();
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
    if (socketRef.current) socketRef.current.disconnect();
  };
  const cleanupWebGazer = () => {
    if (window.webgazer) {
      try {
        // Hide all UI elements first
        const uiElements = ['webgazerVideoFeed', 'webgazerVideoContainer', 'webgazerFaceOverlay', 'webgazerFaceFeedbackBox'];
        uiElements.forEach(id => {
          const el = document.getElementById(id);
          if (el) {
            el.style.display = 'none';
            el.style.visibility = 'hidden';
          }
        });

        // Attempt to clean up methods if they exist
        try {
          if (typeof window.webgazer.clearGazeListener === 'function') {
            window.webgazer.clearGazeListener();
          }
        } catch (e) {
          console.warn('⚠️ Error clearing gaze listener:', e.message);
        }

        try {
          if (typeof window.webgazer.end === 'function') {
            window.webgazer.end();
          }
        } catch (e) {
          console.warn('⚠️ Error ending webgazer:', e.message);
        }

        console.log('✅ WebGazer cleaned up');
      } catch (e) {
        console.warn('⚠️ Error during WebGazer cleanup:', e.message);
      }
    }
  };
  // ==================== ADD RESET BUTTON (OPTIONAL) ====================
  const handleResetCalibration = async () => {
    if (await showConfirm('Reset calibration? You will need to calibrate again.')) {
      console.log('🔄 Resetting calibration...');
      setCalibrationCompleted(false);
      setCurrentStep('calibration');
      localStorage.removeItem(STORAGE_KEY);
    }
  };
  // ==================== RENDER ====================
  if (isCheckingLink) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
        fontFamily: 'Arial', backgroundColor: 'var(--bg-page-flat)', padding: 20,
      }}>
        <div style={{
          maxWidth: 420, width: '100%', backgroundColor: 'var(--bg-card)', borderRadius: 12, padding: 36,
          boxShadow: 'var(--shadow-md)', textAlign: 'center', color: 'var(--text-secondary)', fontWeight: 600,
        }}>
          Verifying interview link...
        </div>
      </div>
    );
  }

  if (currentStep === 'expired') {
    return <LinkExpiredView />;
  }

  if (currentStep === 'name') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-page-flat)',
        fontFamily: "'Inter', sans-serif"
      }}>

        {/* HEADER */}
        <Navbar isLoggedIn showSchedule={false} showCalendar={false} showProfile={false} showLogout={false} />

        {/* MAIN CONTENT */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px'
        }}>

          {/* CARD */}
          <div style={{
            width: '100%',
            maxWidth: '460px',
            background: 'var(--bg-card)',
            padding: '48px 40px',
            borderRadius: '20px',
            boxShadow: '0 14px 34px rgba(15, 23, 42, 0.08)',
            border: '1px solid var(--border-card, rgba(0,0,0,0.06))'
          }}>

            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '16px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 18px', boxShadow: '0 6px 16px rgba(99, 102, 241, 0.25)'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14M5 18h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z" />
                </svg>
              </div>

              <h2 style={{
                margin: '0 0 8px 0', fontSize: '26px', fontWeight: '700',
                color: 'var(--text-primary)',
                fontFamily: "'Outfit', 'Inter', sans-serif"
              }}>
                Join Interview
              </h2>

              <p style={{
                margin: 0, fontSize: '14px',
                color: 'var(--text-muted)', fontWeight: '500'
              }}>
                Step 1 of 3: Enter your name
              </p>
            </div>

            <input
              type="text"
              value={candidateName}
              onChange={(e) => setCandidateName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
              placeholder="Enter your full name"
              autoFocus
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: '12px',
                border: '1px solid var(--border-input, #e5e7eb)',
                marginBottom: '16px',
                background: 'var(--bg-input, #fff)',
                color: 'var(--text-primary)',
                fontSize: '15px',
                fontWeight: '500',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
              }}
            />

            <button
              onClick={handleNameSubmit}
              disabled={!candidateName.trim()}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                background: candidateName.trim()
                  ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                  : '#c7d2fe',
                color: 'white',
                fontWeight: '700',
                fontSize: '15px',
                cursor: candidateName.trim() ? 'pointer' : 'not-allowed',
                boxShadow: candidateName.trim()
                  ? '0 8px 20px rgba(99, 102, 241, 0.3)'
                  : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              Join Interview →
            </button>

          </div>
        </div>

        {/* FOOTER */}
        {/* <Footer /> */}

      </div>
    );
  }
  // STEP 2: CALIBRATION (Skipped)
  if (currentStep === 'calibration') {
    return <div>Skipping calibration...</div>;
  }
  // STEP 3: MEETING
  if (isInterviewEnded) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-page-meeting)', fontFamily: 'Arial', padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'var(--bg-card)', borderRadius: '16px', padding: '50px', maxWidth: '500px', width: '100%',
          boxShadow: 'var(--shadow-lg)', textAlign: 'center'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎉</div>
          <h2 style={{ margin: '0 0 15px 0', color: '#6366f1', fontSize: '28px' }}>Interview Completed!</h2>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '16px', lineHeight: '1.6', margin: 0 }}>
            Thank you for your time, <strong>{candidateName}</strong>. The interviewer has ended the session and your results have been saved successfully.
          </p>
          <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '8px', color: '#2e7d32', fontWeight: 'bold' }}>
            ✅ You may now close this browser tab.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      background: 'var(--bg-page-meeting)',
      minHeight: '100vh',
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Main Video Area */}
      <div style={{
        width: '100%',
        height: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        paddingBottom: '80px', // Space for control bar
        boxSizing: 'border-box',
        gap: '24px'
      }}>
        {/* HUGE REMOTE VIDEO (Interviewers Panel) */}
        <div style={{
          width: activePanel ? '35%' : '100%',
          height: '100%',
          flexShrink: 0,
          backgroundColor: '#000',
          borderRadius: '16px',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          border: '1px solid rgba(255,255,255,0.6)',
          display: 'flex',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          {remoteInterviewers.length === 0 ? (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9aa0a6', fontSize: '18px' }}>
              Waiting for Interviewers to join...
            </div>
          ) : (
            <div style={{ display: 'flex', width: '100%', height: '100%', gap: '4px', backgroundColor: '#202124' }}>
              {remoteInterviewers.map((participant) => (
                <div key={participant.identity} style={{ flex: 1, height: '100%', position: 'relative', minWidth: '300px' }}>
                  <video
                    autoPlay
                    playsInline
                    data-interviewer-id={participant.identity}
                    ref={(el) => {
                      if (el) {
                        // Prefer the stored track (most reliable) — fallback to publication lookup
                        const storedTrack = interviewerVideoTracksRef.current[participant.identity];
                        if (storedTrack) {
                          console.log('📺 Attaching interviewer video track via ref callback');
                          storedTrack.attach(el);
                        } else {
                          const videoPub = Array.from(participant.trackPublications.values()).find(p => p.kind === 'video');
                          if (videoPub && videoPub.videoTrack) {
                            videoPub.videoTrack.attach(el);
                          }
                        }
                      }
                    }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', backgroundColor: '#000' }}
                  />
                  <div style={{
                    position: 'absolute', bottom: '16px', left: '16px', color: 'var(--text-video-label)',
                    backgroundColor: 'var(--bg-video-label)', padding: '6px 12px',
                    borderRadius: '20px', fontSize: '13px', fontWeight: '600', backdropFilter: 'blur(4px)',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center'
                  }}>
                    {mutedParticipants.has(participant.identity) && (
                      <svg viewBox="0 0 24 24" width="14" height="14" stroke="#666" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
                        <line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line>
                      </svg>
                    )}
                    {participant.name ? `${participant.name} (Interviewer)` : 'Interviewer'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* SMALL LOCAL VIDEO OVERLAYS (Candidate / You + Screen Share) */}
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            zIndex: 25,
            alignItems: 'flex-end'
          }}>
            <button
              onClick={() => setIsPipCollapsed(!isPipCollapsed)}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--bg-info-chip)',
                color: 'var(--text-info-chip)',
                border: '1px solid var(--border-control)',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                backdropFilter: 'blur(8px)',
                marginBottom: '2px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
              }}
            >
              {isPipCollapsed ? '👁️ Show You' : '🔽 Minimize'}
            </button>

            <div style={{
              display: isPipCollapsed ? 'none' : 'flex',
              flexDirection: 'column',
              gap: '12px',
              alignItems: 'flex-end'
            }}>
              {isScreenSharing && (
                <div style={{
                  width: isScreenSharing ? '160px' : '200px',
                  height: isScreenSharing ? '100px' : '120px',
                  backgroundColor: '#000',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  position: 'relative',
                  border: '2px solid white',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                  <video
                    ref={localScreenShareRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  />
                  <div style={{ position: 'absolute', bottom: '6px', left: '6px', color: 'var(--text-video-label)', backgroundColor: 'var(--bg-video-label)', padding: '4px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' }}>
                    Your Screen
                  </div>
                </div>
              )}

              <div style={{
                width: isScreenSharing ? '160px' : '200px',
                height: isScreenSharing ? '100px' : '120px',
                backgroundColor: '#000',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                border: '2px solid white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative'
              }}>
                <Webcam
                  id="candidate-local-video"
                  ref={webcamRef}
                  audio={false}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: 'scaleX(-1)' }}
                />
                <div style={{ position: 'absolute', bottom: '6px', left: '6px', color: 'var(--text-video-label)', backgroundColor: 'var(--bg-video-label)', padding: '4px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                  {!isMicEnabled && (
                    <svg viewBox="0 0 24 24" width="12" height="12" stroke="#666" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                      <line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line>
                    </svg>
                  )}
                  You
                </div>
              </div>
            </div>
          </div>
        </div>



        <div style={{
          position: 'absolute', top: '40px', left: '40px', color: '#1e8e3e',
          backgroundColor: 'var(--bg-info-chip)', padding: '8px 16px',
          borderRadius: '12px', fontSize: '13px', fontWeight: '600', zIndex: 10,
          display: 'flex', alignItems: 'center', gap: '8px', backdropFilter: 'blur(8px)',
          border: '1px solid var(--border-control)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
        }}>
          <span style={{ height: '10px', width: '10px', backgroundColor: '#34a853', borderRadius: '50%', display: 'inline-block', boxShadow: '0 0 8px rgba(52, 168, 83, 0.5)' }}></span>
          AI Proctoring Active
        </div>

        {/* Floating Control Bar */}
        <div style={{
          position: 'absolute',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '12px',
          padding: '12px 24px',
          backgroundColor: 'var(--bg-control-bar)',
          backdropFilter: 'blur(16px)',
          borderRadius: '50px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          border: '1px solid var(--border-control)',
          zIndex: 20
        }}>
          <button
            onClick={toggleMic}
            title={isMicEnabled ? "Turn Off Microphone" : "Turn On Microphone"}
            style={{
              width: '48px', height: '48px', borderRadius: '24px',
              backgroundColor: isMicEnabled ? 'var(--bg-control-btn)' : '#ea4335',
              color: isMicEnabled ? '#1976d2' : '#ffffff', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
              border: isMicEnabled ? '1px solid rgba(25,118,210,0.2)' : 'none'
            }}
          >
            {isMicEnabled ? (
              <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
            ) : (
              <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
            )}
          </button>

          <button
            onClick={toggleScreenShare}
            title={isScreenSharing ? "Stop Sharing" : "Share Screen"}
            style={{
              height: '48px', padding: '0 20px', borderRadius: '24px',
              backgroundColor: isScreenSharing ? 'var(--bg-control-btn-active)' : 'var(--bg-control-btn)',
              color: isScreenSharing ? '#1976d2' : 'var(--text-tertiary)', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600',
              transition: 'all 0.2s',
              border: isScreenSharing ? '1px solid rgba(25, 118, 210, 0.3)' : '1px solid var(--border-color)'
            }}
          >
            <span>{isScreenSharing ? '🖥️ Stop Sharing' : '🖥️ Share Screen'}</span>
          </button>

          <div style={{ width: '1px', backgroundColor: 'var(--divider-color)', margin: '0 8px' }}></div>

          {/* ====== WHITEBOARD BUTTON ====== */}
          <button
            onClick={() => togglePanel('whiteboard')}
            title="Toggle Whiteboard"
            style={{
              height: '48px', padding: '0 20px', borderRadius: '24px',
              backgroundColor: activePanel === 'whiteboard' ? 'var(--bg-control-btn-active)' : 'var(--bg-control-btn)',
              color: activePanel === 'whiteboard' ? '#6366f1' : 'var(--text-tertiary)', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600',
              transition: 'all 0.2s',
              border: activePanel === 'whiteboard' ? '1px solid rgba(99,102,241,0.3)' : '1px solid var(--border-color)'
            }}
          >
            🎨 Whiteboard
          </button>

          {/* ====== IDE BUTTON ====== */}
          <button
            onClick={() => togglePanel('ide')}
            title="Toggle Code Editor"
            style={{
              height: '48px', padding: '0 20px', borderRadius: '24px',
              backgroundColor: activePanel === 'ide' ? 'var(--bg-control-btn-active)' : 'var(--bg-control-btn)',
              color: activePanel === 'ide' ? '#22c55e' : 'var(--text-tertiary)', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600',
              transition: 'all 0.2s',
              border: activePanel === 'ide' ? '1px solid rgba(34,197,94,0.3)' : '1px solid var(--border-color)'
            }}
          >
            💻 Code Editor
          </button>

          <div style={{ width: '1px', backgroundColor: 'var(--divider-color)', margin: '0 8px' }}></div>

          <button
            onClick={async () => {
              if (await showConfirm('Are you sure you want to request to end the interview?')) {
                showAlert('Waiting for interviewer to approve...', 'info', 5000);
                if (socketRef.current) {
                  socketRef.current.emit('alert', {
                    roomId,
                    type: 'REQUEST_END_INTERVIEW',
                    message: `${candidateName || 'Candidate'} has requested to end the interview.`,
                    severity: 'info',
                    timestamp: new Date().toISOString()
                  });
                }
              }
            }}
            title="End Interview"
            style={{
              height: '48px', padding: '0 20px', borderRadius: '24px',
              backgroundColor: '#ea4335',
              color: '#ffffff', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(234, 67, 53, 0.3)'
            }}
          >
            <span>🚪 End Interview</span>
          </button>
        </div>

        {/* ====== COLLABORATIVE PANELS OVERLAY ====== */}
        {activePanel && (
          <div style={{
            flex: 1,
            height: '100%',
            position: 'relative',
            zIndex: 15,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'slideUpPanel 0.3s ease-out',
            boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: 'var(--bg-card)'
          }}>
            {showWhiteboard && socketRef.current && (
              <CollaborativeWhiteboard
                socket={socketRef.current}
                roomId={roomId}
                userName={candidateName || propUserName || 'Candidate'}
                role="candidate"
                theme="light"
                onClose={() => togglePanel('whiteboard')}
              />
            )}
            {showIDE && socketRef.current && (
              <CollaborativeIDE
                socket={socketRef.current}
                roomId={roomId}
                userName={candidateName || propUserName || 'Candidate'}
                role="candidate"
                onClose={() => togglePanel('ide')}
              />
            )}
          </div>
        )}
      </div>

      {/* DEBUG PANEL */}
      {/* {currentStep === 'meeting' && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9999,
            padding: '12px',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #ddd',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            fontSize: '12px',
            minWidth: '150px'
          }}
        >
          { <div style={{fontWeight: 'bold'}}>
               Microphone: {listening ? '✅ Listening' : '❌ Stopped'}
               <br/>
               Transcript: {transcript.length} chars
          </div>}
        </div>
      )} */}

      <style>{`
        #interviewer-remote-video,
        #candidate-local-video {
          display: block !important;
          visibility: visible !important;
        }

        button:hover { filter: brightness(0.95); transform: translateY(-1px); }
        button:active { transform: translateY(0); }
        @keyframes slideUpPanel {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
export default CandidateView;